import React from 'react';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import {
  AssignmentOut,
  RouteOption,
  completeAssignment,
  createAssignment,
  getAssignment,
  getAssignments,
  getDrivers,
  getRoutes,
  getVehicles,
} from '../../services/fleet';

const PAGE_SIZE = 50;

type DriverOpt = { id: string; name: string };
type VehicleOpt = { id: string; label: string };

export function Assignments() {
  const [assignments, setAssignments] = React.useState<AssignmentOut[]>([]);
  const [total, setTotal] = React.useState(0);
  const [offset, setOffset] = React.useState(0);
  const [statusFilter, setStatusFilter] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');

  const [selected, setSelected] = React.useState<AssignmentOut | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = React.useState(false);

  const [showCreate, setShowCreate] = React.useState(false);
  const [drivers, setDrivers] = React.useState<DriverOpt[]>([]);
  const [vehicles, setVehicles] = React.useState<VehicleOpt[]>([]);
  const [routes, setRoutes] = React.useState<RouteOption[]>([]);

  const load = React.useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await getAssignments({ status: statusFilter || undefined });
      const list = Array.isArray(response) ? response : (response.assignments ?? []);
      setAssignments(list.slice(offset, offset + PAGE_SIZE));
      setTotal(list.length);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to load assignments');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, offset]);

  React.useEffect(() => { void load(); }, [load]);

  const openCreate = async () => {
    setShowCreate(true);
    try {
      const [driverRes, vehicleRes, routeRes] = await Promise.all([getDrivers(), getVehicles(), getRoutes()]);
      setDrivers(normalizeDriverOpts(driverRes));
      setVehicles(normalizeVehicleOpts(vehicleRes));
      setRoutes(normalizeRouteOpts(routeRes));
    } catch { /* selects stay empty */ }
  };

  const openDetail = async (id: string) => {
    setIsLoadingDetail(true);
    try {
      setSelected(await getAssignment(id));
    } catch {
      setSelected(assignments.find((a) => a.id === id) ?? null);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleComplete = async (assignment: AssignmentOut) => {
    try {
      await completeAssignment(assignment.id);
      setSelected(null);
      setMessage(`Assignment completed.`);
      await load();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to complete assignment');
    }
  };

  const pageStart = total ? offset + 1 : 0;
  const pageEnd = Math.min(offset + assignments.length, total);

  return (
    <div className="assignments-page">
      <div className="section-head drivers-head">
        <div>
          <h2>Assignments</h2>
          <span>{total.toLocaleString()} assignment{total === 1 ? '' : 's'}</span>
        </div>
        <div className="section-actions">
          <FormControl className="assignment-status-filter" size="small">
            <Select
              displayEmpty
              inputProps={{ 'aria-label': 'Filter assignments by status' }}
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setOffset(0);
              }}
            >
              <MenuItem value="">All statuses</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="in_progress">In progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <button className="btn btn-primary" type="button" onClick={() => void openCreate()}>
            <PlusIcon /> New assignment
          </button>
        </div>
      </div>

      {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
      {message ? <div className="kyc-message" role="status">{message}</div> : null}

      <div className="data-table drivers-table">
        <table>
          <thead>
            <tr>
              <th>Route</th>
              <th>Driver</th>
              <th>Vehicle</th>
              <th>Schedule</th>
              <th>Status</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td className="kyc-table-state" colSpan={6}>Loading assignments...</td></tr>
            ) : assignments.length ? assignments.map((a) => (
              <tr key={a.id}>
                <td data-label="Route"><strong>{a.route_name ?? a.route_id ?? 'No route'}</strong></td>
                <td data-label="Driver"><span className="muted text-sm">{a.driver_id}</span></td>
                <td data-label="Vehicle"><span className="kyc-document">{a.vehicle_id}</span></td>
                <td data-label="Schedule">{formatDt(a.start_at)} – {formatDt(a.end_at)}</td>
                <td data-label="Status"><span className={`pill ${statusTone(a.status)}`}>{a.status.replace(/_/g, ' ')}</span></td>
                <td>
                  <div className="passenger-row-actions">
                    <button aria-label="View assignment" className="btn-icon borderless" disabled={isLoadingDetail} title="View" type="button" onClick={() => void openDetail(a.id)}>
                      <EyeIcon />
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td className="kyc-table-state" colSpan={6}>No assignments found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="passengers-pagination">
        <span>Showing {pageStart}–{pageEnd} of {total.toLocaleString()}</span>
        <div>
          <button className="btn" disabled={offset === 0 || isLoading} type="button" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
          <button className="btn" disabled={offset + PAGE_SIZE >= total || isLoading} type="button" onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
        </div>
      </div>

      {selected ? (
        <AssignmentDetail
          assignment={selected}
          onClose={() => setSelected(null)}
          onComplete={() => void handleComplete(selected)}
        />
      ) : null}

      {showCreate ? (
        <CreateAssignmentDialog
          drivers={drivers}
          vehicles={vehicles}
          routes={routes}
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            setMessage('Assignment created successfully.');
            await load();
          }}
        />
      ) : null}
    </div>
  );
}

function AssignmentDetail({ assignment, onClose, onComplete }: { assignment: AssignmentOut; onClose: () => void; onComplete: () => void }) {
  const [confirming, setConfirming] = React.useState(false);
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section aria-labelledby="assignment-detail-title" aria-modal="true" className="signup-dialog driver-details-dialog" role="dialog" onMouseDown={(e) => e.stopPropagation()}>
        <div className="signup-dialog-head">
          <div>
            <span className="vehicle-row-icon"><CalendarIcon /></span>
            <div>
              <h2 id="assignment-detail-title">{assignment.route_name ?? assignment.route_id ?? 'Assignment'}</h2>
              <span className={`pill ${statusTone(assignment.status)}`}>{assignment.status.replace(/_/g, ' ')}</span>
            </div>
          </div>
          <button aria-label="Close" className="btn-icon borderless" type="button" onClick={onClose}><span aria-hidden="true">&times;</span></button>
        </div>
        <dl className="kyc-details">
          <div><dt>Assignment ID</dt><dd className="kyc-document">{assignment.id}</dd></div>
          <div><dt>Driver ID</dt><dd className="kyc-document">{assignment.driver_id}</dd></div>
          <div><dt>Vehicle ID</dt><dd className="kyc-document">{assignment.vehicle_id}</dd></div>
          {assignment.fleet_id ? <div><dt>Fleet ID</dt><dd>{assignment.fleet_id}</dd></div> : null}
          {assignment.route_id ? <div><dt>Route code</dt><dd>{assignment.route_id}</dd></div> : null}
          <div><dt>Start</dt><dd>{formatDtFull(assignment.start_at)}</dd></div>
          <div><dt>End</dt><dd>{formatDtFull(assignment.end_at)}</dd></div>
          {assignment.completed_at ? <div><dt>Completed</dt><dd>{formatDtFull(assignment.completed_at)}</dd></div> : null}
          {assignment.notes ? <div><dt>Notes</dt><dd>{assignment.notes}</dd></div> : null}
          <div><dt>Created</dt><dd>{formatDtFull(assignment.created_at)}</dd></div>
        </dl>
        <div className="kyc-dialog-actions">
          <button className="btn" type="button" onClick={onClose}>Close</button>
          {assignment.status === 'in_progress' ? (
            confirming
              ? <button className="btn btn-primary" type="button" onClick={onComplete}>Confirm complete</button>
              : <button className="btn btn-primary" type="button" onClick={() => setConfirming(true)}>Mark complete</button>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function CreateAssignmentDialog({ drivers, vehicles, routes, onClose, onCreated }: {
  drivers: DriverOpt[];
  vehicles: VehicleOpt[];
  routes: RouteOption[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [driverId, setDriverId] = React.useState('');
  const [vehicleId, setVehicleId] = React.useState('');
  const [routeId, setRouteId] = React.useState('');
  const [routeName, setRouteName] = React.useState('');
  const [startAt, setStartAt] = React.useState('');
  const [endAt, setEndAt] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');

  const selectedRoute = routes.find((r) => r.id === routeId);

  const submit = async () => {
    if (!driverId) { setError('Select a driver'); return; }
    if (!vehicleId) { setError('Select a vehicle'); return; }
    if (!startAt) { setError('Enter a start time'); return; }
    if (!endAt) { setError('Enter an end time'); return; }
    if (endAt <= startAt) { setError('End time must be after start time'); return; }
    setIsSubmitting(true);
    setError('');
    try {
      await createAssignment({
        driver_id: driverId,
        vehicle_id: vehicleId,
        start_at: new Date(startAt).toISOString(),
        end_at: new Date(endAt).toISOString(),
        route_catalog_id: routeId || null,
        route_id: selectedRoute?.route_code ?? null,
        route_name: selectedRoute?.name ?? (routeName.trim() || null),
        notes: notes.trim() || null,
      });
      await onCreated();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to create assignment');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <form
        aria-labelledby="create-assignment-title"
        aria-modal="true"
        className="signup-dialog"
        role="dialog"
        onMouseDown={(e) => e.stopPropagation()}
        onSubmit={(e) => { e.preventDefault(); void submit(); }}
      >
        <div className="signup-dialog-head">
          <div><h2 id="create-assignment-title">New assignment</h2></div>
          <button aria-label="Close" className="btn-icon borderless" type="button" onClick={onClose}><span aria-hidden="true">&times;</span></button>
        </div>
        {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}

        <div className="form-group">
          <label htmlFor="assign-route">Route</label>
          <select id="assign-route" value={routeId} onChange={(e) => setRouteId(e.target.value)}>
            <option value="">— Select a route (optional) —</option>
            {routes.map((r) => <option key={r.id} value={r.id}>{r.route_code} — {r.name}</option>)}
          </select>
        </div>
        {!routeId ? (
          <div className="form-group">
            <label htmlFor="assign-route-name">Route name <span className="optional-label">Optional</span></label>
            <input id="assign-route-name" placeholder="e.g. Lagos Island Morning" value={routeName} onChange={(e) => setRouteName(e.target.value)} />
          </div>
        ) : null}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="assign-driver">Driver</label>
            <select id="assign-driver" required value={driverId} onChange={(e) => setDriverId(e.target.value)}>
              <option value="">Select driver</option>
              {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="assign-vehicle">Vehicle</label>
            <select id="assign-vehicle" required value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
              <option value="">Select vehicle</option>
              {vehicles.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="assign-start">Start time</label>
            <input id="assign-start" required type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
          </div>
          <div className="form-group">
            <label htmlFor="assign-end">End time</label>
            <input id="assign-end" required type="datetime-local" value={endAt} onChange={(e) => setEndAt(e.target.value)} />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="assign-notes">Notes <span className="optional-label">Optional</span></label>
          <textarea id="assign-notes" placeholder="Any additional notes…" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <div className="kyc-dialog-actions">
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={isSubmitting} type="submit">{isSubmitting ? 'Creating…' : 'Create assignment'}</button>
        </div>
      </form>
    </div>
  );
}

// ── normalizers ──────────────────────────────────────────────────────────────

function normalizeDriverOpts(response: unknown): DriverOpt[] {
  return getArray(response, ['drivers', 'items', 'results', 'data']).map((d, i) => ({
    id: readString(d, 'id', 'driver_id', 'user_id') ?? String(i),
    name: readString(d, 'full_name', 'name', 'username', 'email') ?? 'Unknown driver',
  }));
}

function normalizeVehicleOpts(response: unknown): VehicleOpt[] {
  return getArray(response, ['vehicles', 'items', 'results', 'data']).map((v, i) => {
    const reg = readString(v, 'registration_number', 'plate_number', 'plateNumber') ?? `Vehicle ${i + 1}`;
    const make = readString(v, 'make') ?? '';
    const model = readString(v, 'model') ?? '';
    return {
      id: readString(v, 'id', 'vehicle_id') ?? String(i),
      label: [reg, make, model].filter(Boolean).join(' — '),
    };
  });
}

function normalizeRouteOpts(response: unknown): RouteOption[] {
  return getArray(response, ['routes', 'items', 'results', 'data']).map((r, i) => ({
    id: readString(r, 'id', 'route_id') ?? String(i),
    route_code: readString(r, 'route_code', 'code') ?? `R-${i + 1}`,
    name: readString(r, 'name', 'route_name') ?? 'Unnamed route',
    origin_name: readString(r, 'origin_name', 'origin') ?? '',
    destination_name: readString(r, 'destination_name', 'destination') ?? '',
  }));
}

// ── helpers ──────────────────────────────────────────────────────────────────

function statusTone(status: string) {
  if (status === 'in_progress') return 'success';
  if (status === 'scheduled') return 'info';
  if (status === 'completed') return 'muted';
  if (status === 'cancelled') return 'danger';
  return 'muted';
}

function formatDt(value: string | null | undefined) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function formatDtFull(value: string | null | undefined) {
  if (!value) return 'Unknown';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function getArray(value: unknown, keys: string[]): Record<string, unknown>[] {
  if (Array.isArray(value)) return value.filter(isObj);
  if (!isObj(value)) return [];
  for (const key of keys) {
    const nested = value[key];
    if (Array.isArray(nested)) return nested.filter(isObj);
    if (isObj(nested)) {
      const items = getArray(nested, keys);
      if (items.length) return items;
    }
  }
  return [];
}

function readString(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) if (typeof source[key] === 'string' && source[key]) return source[key] as string;
  return undefined;
}

function isObj(v: unknown): v is Record<string, unknown> { return Boolean(v) && typeof v === 'object' && !Array.isArray(v); }

function PlusIcon() { return <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>; }
function EyeIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" /><circle cx="12" cy="12" r="3" /></svg>; }
function CalendarIcon() { return <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18" /></svg>; }
